// src/utils/activityMap.js
// D-297 Team Activity v1 (Option A, Ravi 4 Nov 2026) — the ONE place that knows,
// for every meaningful action, which field holds WHO did it and WHEN.
//
// WHY THIS EXISTS: there is no activity log. Each action leaves its who / when
// on its own document, under its own field names (approved_by / approved_at,
// paid_by / created_at, issued_by_uid / issued_at, ...). The Team Activity page
// reads those collections for the last 7 days and shows, per user, the last
// action and a colour. v1.1 (Ravi 4 Nov): the colour depends on the user's
// ROLE (ROLE_BANDS) and counts WORKING days — Sundays are not counted.
// If a writer renames one of these fields, its action silently stops counting
// and an active person turns red — so the mapping lives here, with Jest cases
// for every action, and nowhere else.
//
// Rules (D-297 decisions, Ravi 4 Nov):
//   - "Active" = a real action, never a login.
//   - Every query is company-scoped (company_id ==) — the read rules prove it.
//   - "who" is matched to a user by the users doc's `uid` field first, then by
//     the users doc id (old rows saved `user.id` — LOW-17). "" never matches.
//   - An action that matches no user is listed, never dropped.
//   - A user with uid "" and no action = "Never logged in", not red.
//   - active === false = inactive, shown greyed, not coloured.
//   - Job accept is not counted (approximate data); job start covers it.
//   - Item request approve / reject count only rows saved with *_by_uid
//     (from c8d8ba7, 4 Nov) — older rows hold a name only.
// Pure: no Firestore import. activityFeedService.js turns the query specs into
// real queries; the page only renders what buildActivityRows() returns.

import { SERVICE_STATUS } from "../constants/enums/serviceEnums";

export const WINDOW_DAYS = 7;
export const QUERY_LIMIT = 500;
const DAY_MS = 24 * 60 * 60 * 1000;

// ─────────────────────────────────────────────
// QUERY SPECS — one per (collection, time field)
// ─────────────────────────────────────────────
// A collection read on two time fields (created_at for "created", updated_at
// for later actions on an older doc) gives the same doc twice; collectActions()
// merges them by collection + doc id.
export const ACTIVITY_SOURCES = Object.freeze([
  { id: "service_logs",          collection: "service_logs",            timeField: "created_at" },
  { id: "mrf_created",           collection: "material_requests",       timeField: "created_at" },
  { id: "mrf_updated",           collection: "material_requests",       timeField: "updated_at" },
  { id: "material_issues",       collection: "material_issues",         timeField: "issued_at" },
  { id: "material_returns",      collection: "material_returns",        timeField: "returned_at" },
  { id: "purchase_requests",     collection: "purchase_requests",       timeField: "created_at" },
  { id: "purchase_orders",       collection: "purchase_orders",         timeField: "updated_at" },
  { id: "purchase_receipts",     collection: "purchase_receipts",       timeField: "created_at" },
  { id: "purchase_bills",        collection: "purchase_bills",          timeField: "created_at" },
  { id: "payments",              collection: "financial_transactions",  timeField: "created_at",
    extraFilters: [{ field: "type", op: "in", value: ["payment", "payment_reversal"] }] },
  { id: "expenses_created",      collection: "site_purchase_requests",  timeField: "created_at" },
  { id: "expenses_updated",      collection: "site_purchase_requests",  timeField: "updated_at" },
  { id: "subcontractor_logs",    collection: "subcontractor_work_logs", timeField: "updated_at" },
  { id: "invoices",              collection: "invoices",                timeField: "created_at" },
  { id: "adjustments_created",   collection: "stock_adjustments",       timeField: "created_at" },
  { id: "adjustments_updated",   collection: "stock_adjustments",       timeField: "updated_at" },
  { id: "item_requests_created", collection: "item_requests",           timeField: "created_at" },
  { id: "item_requests_updated", collection: "item_requests",           timeField: "updated_at" },
]);

/**
 * The real queries, as plain data. Every one starts with company_id == companyId
 * (the read rules are proven from that filter), then time >= since, newest first,
 * at most QUERY_LIMIT docs.
 */
export const activityQueries = (companyId, since) => {
  const company = String(companyId || "").trim();
  if (!company) throw new Error("company_id is required to read team activity");
  if (since === undefined || since === null) throw new Error("since is required to read team activity");
  return ACTIVITY_SOURCES.map((s) => ({
    sourceId:   s.id,
    collection: s.collection,
    filters: [
      { field: "company_id", op: "==", value: company },
      { field: s.timeField,  op: ">=", value: since },
      ...(s.extraFilters || []),
    ],
    orderBy: { field: s.timeField, direction: "desc" },
    limit:   QUERY_LIMIT,
  }));
};

/** true when a source returned a full page — older docs may be missing. */
export const limitHit = (size) => Number(size) >= QUERY_LIMIT;

// ─────────────────────────────────────────────
// TIME
// ─────────────────────────────────────────────
/** Firestore Timestamp / {seconds} / Date / number / ISO string → ms, else null. */
export const toMillis = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof v.toMillis === "function") {
    const ms = v.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof v.seconds === "number") return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  if (typeof v === "string") {
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
};

const startOfLocalDay = (ms) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/** Local midnight (WINDOW_DAYS − 1) days ago: today + the 6 days before it. */
export const windowStart = (nowMs) => {
  const d = new Date(nowMs);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - (WINDOW_DAYS - 1)).getTime();
};

/** Calendar days between the action and today (local time); a future time = 0. */
export const daysAgo = (atMs, nowMs) =>
  Math.max(0, Math.round((startOfLocalDay(nowMs) - startOfLocalDay(atMs)) / DAY_MS));

/**
 * WORKING days between the action and today: the days after the action's day,
 * up to and including today, that are not a Sunday. Same day = 0; Sat → Mon = 1;
 * Fri → Mon = 2; a future time = 0.
 */
export const workingDaysAgo = (atMs, nowMs) => {
  const end = startOfLocalDay(nowMs);
  let d = new Date(startOfLocalDay(atMs));
  let count = 0;
  for (let guard = 0; d.getTime() < end && guard < 400; guard++) {
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    if (d.getDay() !== 0) count++;
  }
  return count;
};

export const BAND = Object.freeze({ GREEN: "green", AMBER: "amber", RED: "red" });

// The page language (commit 5, Ravi 4 Nov): "en" (default on this page) or
// "hi" = Roman Hinglish, from the app-wide useLang() toggle. Anything else = en.
export const langOf = (lang) => (lang === "hi" ? "hi" : "en");

// What each colour / group says on the page. "Inactive" is NOT a band word —
// it is kept for users switched off in User Management.
export const BAND_LABELS = Object.freeze({
  en: Object.freeze({ [BAND.GREEN]: "Using",        [BAND.AMBER]: "Slowing", [BAND.RED]: "Not using" }),
  hi: Object.freeze({ [BAND.GREEN]: "Use kar raha", [BAND.AMBER]: "Dheela",  [BAND.RED]: "Use nahi kar raha" }),
});
const OTHER_GROUP_LABELS = Object.freeze({
  en: Object.freeze({ never: "Never logged in",       inactive: "Inactive users", remind: "Remind" }),
  hi: Object.freeze({ never: "Kabhi login nahi kiya", inactive: "Inactive users", remind: "Yaad dilayein" }),
});

/** green / amber / red / never / inactive → the label in this language ("" for an unknown key). */
export const groupLabel = (key, lang) => {
  const l = langOf(lang);
  return BAND_LABELS[l][key] || OTHER_GROUP_LABELS[l][key] || "";
};

// Working days per role (Ravi 4 Nov): green up to greenMax, amber up to
// amberMax, red after that — or when there is no action in the window.
export const ROLE_BANDS = Object.freeze({
  technician:    { greenMax: 1, amberMax: 2 },
  store:         { greenMax: 1, amberMax: 3 },
  site_engineer: { greenMax: 2, amberMax: 4 },
  manager:       { greenMax: 2, amberMax: 4 },
  sm:            { greenMax: 2, amberMax: 4 },
  accounts:      { greenMax: 3, amberMax: 6 },
  owner:         { greenMax: 3, amberMax: 6 },
});
// "tech" = technician, the same as Dashboard.js treats it.
const ROLE_ALIASES = Object.freeze({ tech: "technician" });
const DEFAULT_BAND_ROLE = "manager"; // any other / unknown role (Ravi 4 Nov)

/** The ROLE_BANDS key whose bands apply to this role (trimmed, lower-cased: "STORE" = store). */
export const bandRoleOf = (role) => {
  const r = String(role ?? "").trim().toLowerCase();
  const key = ROLE_ALIASES[r] || r;
  return Object.prototype.hasOwnProperty.call(ROLE_BANDS, key) ? key : DEFAULT_BAND_ROLE;
};

export const bandsFor = (role) => ROLE_BANDS[bandRoleOf(role)];

/** The colour for this role: working days against ROLE_BANDS; no action = red. */
export const dayBand = (atMs, nowMs, role) => {
  if (atMs === null || atMs === undefined) return BAND.RED;
  const days = workingDaysAgo(atMs, nowMs);
  const { greenMax, amberMax } = bandsFor(role);
  if (days <= greenMax) return BAND.GREEN;
  if (days <= amberMax) return BAND.AMBER;
  return BAND.RED;
};

const range = (from, to) => (from === to ? `${from}` : `${from}–${to}`);

const WORKING_DAYS_UNIT = Object.freeze({ en: "working days", hi: "kaam ke din" });

/** "Using 0–1 · Slowing 2 · Not using 3+ working days" (or Hinglish) for one band set. */
export const bandText = ({ greenMax, amberMax }, lang) => {
  const l = langOf(lang);
  const L = BAND_LABELS[l];
  return `${L.green} ${range(0, greenMax)} · ${L.amber} ${range(greenMax + 1, amberMax)} · ` +
    `${L.red} ${amberMax + 1}+ ${WORKING_DAYS_UNIT[l]}`;
};

/**
 * One legend line per band set used by the roles present (roles with the same
 * bands share a line), in ROLE_BANDS order.
 * @returns {Array<{ roles: string[], greenMax, amberMax, text }>}
 */
export const legendFor = (roles, lang) => {
  const present = new Set((roles || []).map(bandRoleOf));
  const lines = [];
  for (const key of Object.keys(ROLE_BANDS)) {
    if (!present.has(key)) continue;
    const b = ROLE_BANDS[key];
    const same = lines.find((l) => l.greenMax === b.greenMax && l.amberMax === b.amberMax);
    if (same) { same.roles.push(key); continue; }
    lines.push({ roles: [key], greenMax: b.greenMax, amberMax: b.amberMax, text: bandText(b, lang) });
  }
  return lines;
};

const hhmm = (ms) => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const WHEN_WORDS = Object.freeze({
  en: Object.freeze({ none: `${WINDOW_DAYS}+ days — no action`, today: "today", yesterday: "yesterday",
    day: (w) => `${w} working day${w === 1 ? "" : "s"}` }),
  hi: Object.freeze({ none: `${WINDOW_DAYS}+ din — koi entry nahi`, today: "aaj", yesterday: "kal",
    day: (w) => `${w} kaam ${w === 1 ? "ka" : "ke"} din` }),
});

/**
 * "today 14:05" / "yesterday 18:20" / "Sat 07 Nov · 3 working days" /
 * "7+ days — no action" (Hinglish: "aaj 14:05" / "kal 18:20" /
 * "Sat 07 Nov · 3 kaam ke din" / "7+ din — koi entry nahi"). Older than
 * yesterday shows the date and the WORKING days, so the number never
 * contradicts the colour.
 */
export const whenText = (atMs, nowMs, lang) => {
  const W = WHEN_WORDS[langOf(lang)];
  if (atMs === null || atMs === undefined) return W.none;
  const days = daysAgo(atMs, nowMs);
  if (days === 0) return `${W.today} ${hhmm(atMs)}`;
  if (days === 1) return `${W.yesterday} ${hhmm(atMs)}`;
  const d = new Date(atMs);
  const w = workingDaysAgo(atMs, nowMs);
  return `${WEEKDAYS[d.getDay()]} ${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} · ${W.day(w)}`;
};

// ─────────────────────────────────────────────
// ACTIONS — who / when per collection
// ─────────────────────────────────────────────
// who: fields tried in order, the first non-empty one is the actor.
// whoName: the matching display-name field (shown only for unmatched actions).
// applies: optional — the action exists on this doc only when it returns true.

const serviceLogLabel = (d) => {
  const from = d.from_status;
  const to   = d.to_status;
  if (from && from === to) return "Job declined";
  if (to === SERVICE_STATUS.IN_PROGRESS && from === SERVICE_STATUS.COMPLETED_PENDING_APPROVAL) {
    return "Job completion sent back";
  }
  switch (to) {
    case SERVICE_STATUS.ASSIGNED:                   return "Job assigned";
    case SERVICE_STATUS.IN_PROGRESS:                return "Job started";
    case SERVICE_STATUS.PARTIAL:                    return "Job partly done";
    case SERVICE_STATUS.COMPLETED_PENDING_APPROVAL: return "Job completed";
    case SERVICE_STATUS.CLOSED:                     return "Job closed";
    case SERVICE_STATUS.CANCELLED:                  return "Job cancelled";
    default:                                        return `Job status changed to ${to || "unknown"}`;
  }
};

export const ACTIONS_BY_COLLECTION = Object.freeze({
  service_logs: [
    { key: "job_status", label: serviceLogLabel, who: ["changed_by"], whoName: "changed_by_name", at: "created_at" },
  ],
  material_requests: [
    { key: "mrf_raised",   label: "MRF raised",   who: ["requested_by"], whoName: "requested_by_name", at: "created_at",
      ref: (d) => d.project_name || d.site_name || "" },
    { key: "mrf_approved", label: "MRF approved", who: ["approved_by"],  whoName: "approved_by_name",  at: "approved_at",
      ref: (d) => d.project_name || d.site_name || "" },
  ],
  material_issues: [
    { key: "stock_issued", label: "Stock issued", who: ["issued_by_uid", "issued_by"], whoName: "issued_by_name", at: "issued_at",
      ref: (d) => d.site_name || "" },
  ],
  material_returns: [
    { key: "material_returned", label: "Material returned", who: ["returned_by"], whoName: "returned_by_name", at: "returned_at",
      ref: (d) => d.site_name || "" },
  ],
  purchase_requests: [
    { key: "pr_created", label: "PR created", who: ["created_by"], whoName: "created_by_name", at: "created_at",
      ref: (d) => d.purchase_request_no || "" },
  ],
  purchase_orders: [
    { key: "po_created",        label: "PO created",           who: ["created_by"],        whoName: "created_by_name",        at: "created_at",
      ref: (d) => d.po_number || "" },
    { key: "po_approved",       label: "PO approved",          who: ["approved_by"],       whoName: "approved_by_name",       at: "approved_at",
      ref: (d) => d.po_number || "" },
    { key: "po_ordered",        label: "PO marked as ordered", who: ["ordered_by"],        whoName: "ordered_by_name",        at: "ordered_at",
      ref: (d) => d.po_number || "" },
    { key: "po_site_confirmed", label: "Site receipt confirmed", who: ["site_confirmed_by"], whoName: "site_confirmed_by_name", at: "site_confirmed_at",
      ref: (d) => d.po_number || "" },
  ],
  purchase_receipts: [
    { key: "goods_received",
      label: (d) => (d.is_direct_site_purchase === true ? "Direct Site finalized" : "Goods received (GRN)"),
      who: ["completed_by"], whoName: "completed_by_name", at: "created_at",
      ref: (d) => d.po_number || "" },
  ],
  purchase_bills: [
    { key: "bill_entered", label: "Purchase bill entered", who: ["created_by"], whoName: "created_by_name", at: "created_at",
      ref: (d) => [d.vendor_name, d.vendor_invoice_no || (d.bill_type === "proforma" ? "Proforma" : "")].filter(Boolean).join(" · ") },
  ],
  financial_transactions: [
    { key: "payment_recorded", label: "Payment recorded", who: ["paid_by"],    whoName: "paid_by_name",    at: "created_at",
      applies: (d) => d.type === "payment",          ref: (d) => d.party_name || "" },
    { key: "payment_reversed", label: "Payment reversed", who: ["created_by"], whoName: "created_by_name", at: "created_at",
      applies: (d) => d.type === "payment_reversal", ref: (d) => d.party_name || "" },
  ],
  site_purchase_requests: [
    { key: "expense_submitted", label: "Expense submitted", who: ["requested_by", "requested_by_id"], whoName: "requested_by_name", at: "created_at",
      ref: (d) => d.description || "" },
    { key: "expense_approved",  label: "Expense approved",  who: ["approved_by"], whoName: "approved_by_name", at: "approved_at",
      ref: (d) => d.description || "" },
    { key: "expense_rejected",  label: "Expense rejected",  who: ["rejected_by"], whoName: "rejected_by_name", at: "rejected_at",
      ref: (d) => d.description || "" },
  ],
  subcontractor_work_logs: [
    { key: "subcontractor_confirmed", label: "Subcontractor work confirmed", who: ["confirmed_by"], whoName: "confirmed_by_name", at: "confirmed_at",
      ref: (d) => d.subcontractor_name || "" },
    { key: "subcontractor_rejected",  label: "Subcontractor work rejected",  who: ["rejected_by"],  whoName: "rejected_by_name",  at: "rejected_at",
      ref: (d) => d.subcontractor_name || "" },
  ],
  invoices: [
    { key: "invoice_created", label: "Invoice created", who: ["created_by"], whoName: "created_by_name", at: "created_at",
      ref: (d) => d.invoice_no || "" },
  ],
  stock_adjustments: [
    { key: "adjustment_submitted", label: "Stock adjustment submitted", who: ["submitted_by"], whoName: "submitted_by_name", at: "created_at",
      ref: (d) => d.adjustment_no || "" },
    { key: "adjustment_approved",  label: "Stock adjustment approved",  who: ["approved_by"],  whoName: "approved_by_name",  at: "approved_at",
      ref: (d) => d.adjustment_no || "" },
    { key: "adjustment_rejected",  label: "Stock adjustment rejected",  who: ["rejected_by"],  whoName: "rejected_by_name",  at: "rejected_at",
      ref: (d) => d.adjustment_no || "" },
  ],
  item_requests: [
    { key: "item_requested", label: "New item requested", who: ["requested_by_uid"], whoName: "requested_by_name", at: "created_at",
      ref: (d) => d.item_name || "" },
    // Only rows saved from c8d8ba7 carry approved_by_uid / rejected_by_uid;
    // approved_by / rejected_by hold a NAME and are never used as "who".
    { key: "item_request_approved",
      label: (d) => (d.status === "merged" ? "Item request merged" : "Item request approved"),
      who: ["approved_by_uid"], whoName: "approved_by", at: "approved_at",
      ref: (d) => d.canonical_name || d.merged_item_name || d.item_name || "" },
    { key: "item_request_rejected", label: "Item request rejected", who: ["rejected_by_uid"], whoName: "rejected_by", at: "rejected_at",
      ref: (d) => d.item_name || "" },
  ],
});

const firstFilled = (data, fields) => {
  for (const f of fields) {
    const v = data[f];
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return "";
};

/**
 * Every action inside the window from the docs the queries returned.
 * @param {Object} docsBySource  { [sourceId]: [{ id, data }] }
 * @param {number} sinceMs       windowStart(now)
 * @returns {Array<{key, collection, docId, action, label, who, whoName, atMs, ref}>}
 *          newest first; a doc seen by two queries is counted once.
 */
export const collectActions = (docsBySource, sinceMs) => {
  const collectionOf = Object.fromEntries(ACTIVITY_SOURCES.map((s) => [s.id, s.collection]));
  const docs = new Map(); // `${collection}/${docId}` → { collection, docId, data }
  for (const [sourceId, list] of Object.entries(docsBySource || {})) {
    const collection = collectionOf[sourceId];
    if (!collection) throw new Error(`Unknown activity source: ${sourceId}`);
    for (const item of list || []) {
      if (!item || !item.id) continue;
      docs.set(`${collection}/${item.id}`, { collection, docId: item.id, data: item.data || {} });
    }
  }

  const out = [];
  for (const { collection, docId, data } of docs.values()) {
    for (const def of ACTIONS_BY_COLLECTION[collection] || []) {
      if (def.applies && !def.applies(data)) continue;
      const who = firstFilled(data, def.who);
      if (!who) continue;
      const atMs = toMillis(data[def.at]);
      if (atMs === null || atMs < sinceMs) continue;
      out.push({
        key:     `${collection}/${docId}/${def.key}`,
        collection,
        docId,
        action:  def.key,
        label:   typeof def.label === "function" ? def.label(data) : def.label,
        who,
        whoName: String(data[def.whoName] || ""),
        atMs,
        ref:     def.ref ? String(def.ref(data) || "") : "",
      });
    }
  }
  return out.sort((a, b) => b.atMs - a.atMs);
};

// ─────────────────────────────────────────────
// USERS
// ─────────────────────────────────────────────
/** users: [{ docId, uid, name, role, active, ... }] → lookups by uid and by doc id. */
export const buildUserIndex = (users) => {
  const byUid = new Map();
  const byDocId = new Map();
  for (const u of users || []) {
    if (!u) continue;
    const uid = String(u.uid || "").trim();
    if (uid) byUid.set(uid, u);
    if (u.docId) byDocId.set(String(u.docId), u);
  }
  return { byUid, byDocId };
};

/** The users doc an actor id belongs to: uid first, then doc id (LOW-17); "" → null. */
export const matchActor = (id, index) => {
  const key = String(id || "").trim();
  if (!key || !index) return null;
  return index.byUid.get(key) || index.byDocId.get(key) || null;
};

const BAND_ORDER = { [BAND.RED]: 0, [BAND.AMBER]: 1, [BAND.GREEN]: 2 };

/**
 * One row per user for the page.
 * @returns {{
 *   rows:           Array<{user, band, last, recent, whenText}>  active users, red → amber → green, stalest first
 *   neverLoggedIn:  Array<{user}>                                uid "" and no action
 *   inactive:       Array<{user, last, recent, whenText}>        active === false, not coloured
 *   unmatched:      Array<action>                                actions whose actor is no user of this company
 *   counts:         {green, amber, red, neverLoggedIn, inactive, unmatched}
 * }}
 */
export const buildActivityRows = ({ users, actions, nowMs, lang }) => {
  const index = buildUserIndex(users);
  const byUser = new Map(); // users doc id (or uid) → actions, newest first
  const unmatched = [];

  const userKey = (u) => String(u.docId || u.uid);
  const sorted = [...(actions || [])].sort((a, b) => b.atMs - a.atMs);
  for (const a of sorted) {
    const u = matchActor(a.who, index);
    if (!u) { unmatched.push(a); continue; }
    const k = userKey(u);
    if (!byUser.has(k)) byUser.set(k, []);
    byUser.get(k).push(a);
  }

  const rows = [];
  const neverLoggedIn = [];
  const inactive = [];
  for (const u of users || []) {
    if (!u) continue;
    const mine = byUser.get(userKey(u)) || [];
    const last = mine[0] || null;
    const recent = mine.slice(0, 5);
    if (u.active === false) {
      inactive.push({ user: u, last, recent, whenText: whenText(last ? last.atMs : null, nowMs, lang) });
      continue;
    }
    if (!String(u.uid || "").trim() && mine.length === 0) {
      neverLoggedIn.push({ user: u });
      continue;
    }
    const atMs = last ? last.atMs : null;
    rows.push({ user: u, band: dayBand(atMs, nowMs, u.role), last, recent, whenText: whenText(atMs, nowMs, lang) });
  }

  rows.sort((a, b) => {
    const byBand = BAND_ORDER[a.band] - BAND_ORDER[b.band];
    if (byBand !== 0) return byBand;
    const aAt = a.last ? a.last.atMs : -Infinity; // no action = stalest
    const bAt = b.last ? b.last.atMs : -Infinity;
    if (aAt !== bAt) return aAt - bAt;
    return String(a.user.name || "").localeCompare(String(b.user.name || ""));
  });

  const count = (band) => rows.filter((r) => r.band === band).length;
  return {
    rows,
    neverLoggedIn,
    inactive,
    unmatched,
    counts: {
      green:         count(BAND.GREEN),
      amber:         count(BAND.AMBER),
      red:           count(BAND.RED),
      neverLoggedIn: neverLoggedIn.length,
      inactive:      inactive.length,
      unmatched:     unmatched.length,
    },
  };
};

// ─────────────────────────────────────────────
// CHIP FILTERS (commit 5, Ravi 4 Nov)
// ─────────────────────────────────────────────
// Tap a chip = show only that group; tap the same chip again = show all.
// "remind" (commit 7) has no chip: the Home card's "Remind N →" opens
// /team-activity?filter=remind = the red rows + the Never logged in section,
// WITHOUT the Owner looking — exactly the people the card counts.
export const CHIP_FILTERS = Object.freeze([BAND.RED, BAND.AMBER, BAND.GREEN, "never", "inactive"]);
export const FILTERS = Object.freeze([...CHIP_FILTERS, "remind"]);

/** The filter after a chip tap: the same chip again → null (all), else the tapped one. */
export const toggleFilter = (current, tapped) =>
  (!CHIP_FILTERS.includes(tapped) || current === tapped ? null : tapped);

/**
 * What the page shows for a filter.
 * @param {Object} viewer  { uid, docId } of the Owner looking — left out of "remind" only
 * @returns {{ filter, rows, showRows, showNever, openNever, showInactive, openInactive, showUnmatched, empty, count }}
 *   empty = a filter is on and its group has nobody ("Nobody here");
 *   count = how many people the filter shows (for "remind" = the card's remind).
 */
export const filterView = (result, filter, viewer) => {
  const r = result || { rows: [], neverLoggedIn: [], inactive: [], unmatched: [] };
  const f = FILTERS.includes(filter) ? filter : null;
  const none = { filter: f, rows: [], showRows: false, showNever: false, openNever: false,
    showInactive: false, openInactive: false, showUnmatched: false, empty: false, count: 0 };
  if (f === null) {
    return { ...none, rows: r.rows, showRows: true,
      showNever: r.neverLoggedIn.length > 0, showInactive: r.inactive.length > 0,
      showUnmatched: r.unmatched.length > 0, count: r.rows.length };
  }
  if (f === "remind") {
    const rows = r.rows.filter((row) => row.band === BAND.RED && !sameUser(row.user, viewer));
    const never = r.neverLoggedIn.filter((n) => !sameUser(n.user, viewer));
    return { ...none, rows, showRows: rows.length > 0, showNever: never.length > 0, openNever: true,
      empty: rows.length === 0 && never.length === 0, count: rows.length + never.length };
  }
  if (f === "never") {
    return { ...none, showNever: r.neverLoggedIn.length > 0, openNever: true, empty: r.neverLoggedIn.length === 0,
      count: r.neverLoggedIn.length };
  }
  if (f === "inactive") {
    return { ...none, showInactive: r.inactive.length > 0, openInactive: true, empty: r.inactive.length === 0,
      count: r.inactive.length };
  }
  const rows = r.rows.filter((row) => row.band === f);
  return { ...none, rows, showRows: rows.length > 0, empty: rows.length === 0, count: rows.length };
};

// ─────────────────────────────────────────────
// HOME CARD — "Team on Zeno: using / total" (commit 7, Ravi 4 Nov)
// ─────────────────────────────────────────────
const sameUser = (u, viewer) => {
  if (!u || !viewer) return false;
  const uid = String(viewer.uid || "").trim();
  const docId = String(viewer.docId || "").trim();
  return (uid !== "" && (u.uid === uid || u.docId === uid)) || (docId !== "" && u.docId === docId);
};

/**
 * The counts the Home score needs, with the Owner who is LOOKING left out
 * (matched by uid or users doc id). Inactive users are never in them.
 * @returns {{ green, amber, red, neverLoggedIn }}
 */
export const scoreCounts = (result, viewer) => {
  const rows = (result?.rows || []).filter((r) => !sameUser(r.user, viewer));
  const never = (result?.neverLoggedIn || []).filter((n) => !sameUser(n.user, viewer));
  const count = (band) => rows.filter((r) => r.band === band).length;
  return { green: count(BAND.GREEN), amber: count(BAND.AMBER), red: count(BAND.RED), neverLoggedIn: never.length };
};

/**
 * The Home card's score. using = green + amber; total = green + amber + red +
 * never logged in; remind = red + never logged in; pct rounded; band on the
 * shown pct: ≥ 80 green, 50–79 amber, < 50 red. total 0 → null (no card).
 */
export const teamScore = (counts) => {
  const n = (k) => Math.max(0, Math.floor(Number(counts?.[k]) || 0));
  const using = n("green") + n("amber");
  const remind = n("red") + n("neverLoggedIn");
  const total = using + remind;
  if (total === 0) return null;
  const pct = Math.round((using * 100) / total);
  const band = pct >= 80 ? BAND.GREEN : pct >= 50 ? BAND.AMBER : BAND.RED;
  return { using, total, remind, pct, band };
};

// The Home card computes the counts once and keeps them in sessionStorage for
// 30 minutes (option B, Ravi 4 Nov) — later Home loads read nothing. Only a
// COMPLETE result (every source read) is cached, so a failure is retried.
// v2 (commit 7): the four score counts, without the Owner looking; the entry
// belongs to that viewer (another login in the same tab never reuses it).
export const CACHE_TTL_MS = 30 * 60 * 1000;
export const CACHE_KEY = "zeno_team_activity_counts";
const CACHE_VERSION = 2;
const SCORE_KEYS = ["green", "amber", "red", "neverLoggedIn"];

const viewerKeyOf = (viewer) => String(viewer?.uid || viewer?.docId || "").trim();

export const cacheEntry = ({ companyId, viewer, nowMs, counts }) => ({
  v:         CACHE_VERSION,
  companyId: String(companyId || ""),
  viewer:    viewerKeyOf(viewer),
  savedAt:   nowMs,
  counts:    Object.fromEntries(SCORE_KEYS.map((k) => [k, Number(counts?.[k] || 0)])),
});

/** The stored text → an entry, or null when missing / not JSON. */
export const readCacheEntry = (raw) => {
  if (typeof raw !== "string" || raw === "") return null;
  try { return JSON.parse(raw); } catch (e) { return null; }
};

/** Same company AND viewer, version 2, all four counts, saved less than 30 minutes ago (not in the future). */
export const isCacheFresh = (entry, nowMs, companyId, viewer) => {
  if (!entry || typeof entry !== "object") return false;
  if (entry.v !== CACHE_VERSION) return false;
  if (!companyId || entry.companyId !== companyId) return false;
  const v = viewerKeyOf(viewer);
  if (!v || entry.viewer !== v) return false;
  if (typeof entry.savedAt !== "number" || !Number.isFinite(entry.savedAt)) return false;
  const age = nowMs - entry.savedAt;
  if (age < 0 || age >= CACHE_TTL_MS) return false;
  return SCORE_KEYS.every((k) => Number.isFinite(entry.counts?.[k]));
};
